import { useMutation } from '@tanstack/react-query';
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  DateInput,
  ImageField,
  Loading,
  ReferenceArrayInput,
  ReferenceInput,
  TextInput,
} from '../../components/admin';
import { FormToolbar } from '../../components/admin/simple-form';
import { SaveButton } from '../../components/admin/form';
import { Button } from '../../components/ui/button';
import { Ban, Check, KeyRound } from 'lucide-react';
import {
  useDataProvider,
  useGetOne,
  useGetRecordId,
  useNotify,
  useRecordContext,
  useRefresh,
  useResourceContext,
  useTranslate,
  useTranslateLabel,
} from 'ra-core';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import { MoonstarEdit } from '../Moonstar/MoonstarEdit';
import { SimpleStack } from '../Moonstar/SimpleStack';
import { MoonstarEditForm } from '../Moonstar/MoonstarEditForm';
import { phoneNumber } from '../../utils/validation';
import { getErrorMessage } from './getErrorMessage';
import { getAdminUserDisplayName } from './types';
import { useHasPermission } from '../../auth/useHasPermission';
import { required } from 'ra-core';

export const UserEdit = () => {
  const translate = useTranslate();
  const shouldBeOptional = useHasPermission(
    'OPTIONAL_USER_ONBOARDING_FIELDS_ACCESS'
  );
  const recordId = useGetRecordId();
  const { data: record, isLoading } = useGetOne('users', {
    id: recordId,
    meta: { force: true },
  });

  const dataProvider = useDataProvider<AdminDataProvider>();

  const validatePhoneNumberExists = async (value: string, id: string) => {
    if (!value) return undefined;
    try {
      const response = await dataProvider.getUserByPhoneNumber(value);
      if (response && response.id.toString() !== id) {
        return `This phone number is already taken by user: ${getAdminUserDisplayName(response)}`;
      }
    } catch (error) {
      console.error('Phone number validation error:', error);
      return 'Error checking phone number availability';
    }
    return undefined;
  };

  const validateInternalIdExists = async (value: string, id: string) => {
    if (!value) return undefined;
    try {
      const response = await dataProvider.getUserByInternalId(value);
      if (response && response.id.toString() !== id) {
        return `This internal identifier is already taken by user: ${getAdminUserDisplayName(response)}`;
      }
    } catch (error) {
      return getErrorMessage(error);
    }
    return undefined;
  };

  if (isLoading || !record) return <Loading />;

  return (
    <MoonstarEdit
      redirect="list"
      hideDelete
      transform={transformOptionalUserFields}
    >
      <MoonstarEditForm toolbar={<UserEditToolbar />}>
        <div className="flex flex-wrap gap-4">
          <SimpleStack>
            <h2 className="text-lg font-semibold">
              {translate('moonstar.user.edit.section.personalDetails')}
            </h2>
            <ImageField
              source="pictureUrl"
              title="Profile picture"
              className="[&_img]:max-h-[160px] [&_img]:max-w-[160px] [&_img]:object-contain [&_img]:object-left"
            />
            <TextInput
              source="firstName"
              validate={shouldBeOptional ? undefined : required()}
              format={(value) => value ?? ''}
              parse={(value) => value ?? ''}
            />
            <TextInput
              source="lastName"
              validate={shouldBeOptional ? undefined : required()}
              format={(value) => value ?? ''}
              parse={(value) => value ?? ''}
            />
            <PhoneNumberEdit
              disabledFrom={record.disabledFrom}
              initialValue={record.phoneNumber}
              signIn={record.signIn.phoneNumber}
              recordId={recordId}
              validatePhoneNumberExists={validatePhoneNumberExists}
            />
            <EmailEdit
              disabledFrom={record.disabledFrom}
              initialValue={record.email}
              signIn={record.signIn.email}
            />
          </SimpleStack>

          <SimpleStack>
            <h2 className="text-lg font-semibold">
              {translate('moonstar.user.edit.section.companyRelatedInfo')}
            </h2>
            <TextInput
              source="internalId"
              label="Internal ID"
              validate={[
                async (value) =>
                  validateInternalIdExists(value, recordId.toString()),
              ]}
              format={(value) => value ?? ''}
              parse={(value) => value ?? ''}
            />
            <ReferenceInput
              source="jobTitleId"
              reference="jobTitles"
              perPage={999}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <AutocompleteInput
                optionText="name"
                validate={shouldBeOptional ? undefined : required()}
                format={(value) => value ?? ''}
                parse={(value) => value ?? ''}
                filterToQuery={(query: string) => ({ name: query })}
              />
            </ReferenceInput>
            <ReferenceInput
              source="departmentId"
              reference="departments"
              perPage={999}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <AutocompleteInput
                optionText="name"
                validate={shouldBeOptional ? undefined : required()}
                format={(value) => value ?? ''}
                parse={(value) => value ?? ''}
                filterToQuery={(query: string) => ({ name: query })}
              />
            </ReferenceInput>
            <ReferenceInput
              source="regionId"
              reference="regions"
              perPage={999}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <AutocompleteInput
                optionText="name"
                validate={shouldBeOptional ? undefined : required()}
                format={(value) => value ?? ''}
                parse={(value) => value ?? ''}
                filterToQuery={(query: string) => ({ name: query })}
              />
            </ReferenceInput>
            <ReferenceArrayInput
              source="unitIds"
              reference="units"
              perPage={999}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <AutocompleteArrayInput
                optionText="name"
                validate={required()}
                filterToQuery={(query: string) => ({ name: query })}
              />
            </ReferenceArrayInput>
            <DateInput
              source="startDate"
              validate={required()}
              parse={parseDate}
            />
          </SimpleStack>

          <SimpleStack>
            <h2 className="text-lg font-semibold">
              {translate('moonstar.user.edit.section.profileSettings')}
            </h2>
            <TextInput source="language" disabled />
          </SimpleStack>
        </div>
      </MoonstarEditForm>
    </MoonstarEdit>
  );
};

const UserEditToolbar = () => {
  const record = useRecordContext<{
    id: string | number;
    disabledFrom?: string;
  }>();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const isUserDisabled = Boolean(record?.disabledFrom);
  const setUserEnabled = useMutation({
    mutationFn: () =>
      dataProvider.setUserEnabled(record!.id.toString(), isUserDisabled),
    onSuccess: () => {
      notify(
        isUserDisabled
          ? 'moonstar.user.edit.enable.success'
          : 'moonstar.user.edit.disable.success',
        { type: 'success' }
      );
      refresh();
    },
    onError: () => {
      notify(
        isUserDisabled
          ? 'moonstar.user.edit.enable.error'
          : 'moonstar.user.edit.disable.error',
        { type: 'error' }
      );
    },
  });

  return (
    <FormToolbar>
      <div className="flex flex-row justify-end gap-2">
        <SaveButton />
        <Button
          type="button"
          variant={isUserDisabled ? 'outline' : 'destructive'}
          disabled={setUserEnabled.isPending}
          onClick={() => setUserEnabled.mutate()}
        >
          {isUserDisabled ? <Check /> : <Ban />}
          {translate(
            isUserDisabled
              ? 'moonstar.user.edit.enable.label'
              : 'moonstar.user.edit.disable.label'
          )}
        </Button>
      </div>
    </FormToolbar>
  );
};

export const parseDate = (value: string | number | Date) => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

const transformOptionalUserFields = (data: Record<string, unknown>) => ({
  ...data,
  firstName: data.firstName || undefined,
  lastName: data.lastName || undefined,
  internalId: data.internalId || undefined,
  departmentId: data.departmentId || undefined,
  jobTitleId: data.jobTitleId || undefined,
  regionId: data.regionId || undefined,
});

type SignInUserProps = {
  disabledFrom?: string | null;
  initialValue?: string;
  signIn?: {
    enabled: boolean;
    value?: string;
  };
};

type PhoneSignInUserProps = SignInUserProps & {
  recordId: any;
  validatePhoneNumberExists: (
    value: string,
    recordId: string
  ) => Promise<string | undefined>;
};

const PhoneNumberEdit = ({
  disabledFrom,
  initialValue,
  signIn,
  recordId,
  validatePhoneNumberExists,
}: PhoneSignInUserProps) => {
  const userDisabled = disabledFrom != null;
  const phoneValidation = signIn?.enabled
    ? [
        required('moonstar.user.edit.phoneNumber.requiredText'),
        phoneNumber(),
        async (value: any) =>
          validatePhoneNumberExists(value, recordId.toString()),
      ]
    : [
        phoneNumber(),
        async (value: any) =>
          validatePhoneNumberExists(value, recordId.toString()),
      ];

  if (signIn?.enabled) {
    const phoneNumberMatches = initialValue === signIn.value;
    const helperText = userDisabled ? undefined : phoneNumberMatches ? (
      'moonstar.user.edit.phoneNumber.enabledHelperText'
    ) : (
      <NotMatchingSignInHelperText value={signIn.value} />
    );

    return (
      <TextInput
        source="phoneNumber"
        label={<SignInMethodLabel source="phoneNumber" isRequired />}
        validate={phoneValidation}
        helperText={helperText}
      />
    );
  }

  return (
    <TextInput
      source="phoneNumber"
      validate={phoneValidation}
      helperText={
        userDisabled
          ? undefined
          : 'moonstar.user.edit.phoneNumber.disabledHelperText'
      }
    />
  );
};

const EmailEdit = ({ disabledFrom, initialValue, signIn }: SignInUserProps) => {
  const userDisabled = disabledFrom != null;

  if (signIn?.enabled) {
    const emailMatches = initialValue === signIn.value;
    const helperText = userDisabled ? undefined : emailMatches ? (
      'moonstar.user.edit.email.enabledHelperText'
    ) : (
      <NotMatchingSignInHelperText value={signIn.value} />
    );
    return (
      <TextInput
        source="email"
        label={<SignInMethodLabel source="email" />}
        disabled
        helperText={helperText}
      />
    );
  }

  return (
    <TextInput
      source="email"
      disabled
      helperText={
        userDisabled ? undefined : 'moonstar.user.edit.email.disabledHelperText'
      }
    />
  );
};

/** The field label with a key icon marking the field used to sign in */
const SignInMethodLabel = ({
  source,
  isRequired,
}: {
  source: string;
  isRequired?: boolean;
}) => {
  const translate = useTranslate();
  const translateLabel = useTranslateLabel();
  const resource = useResourceContext();

  return (
    <span className="inline-flex items-center gap-1">
      {translateLabel({ source, resource })}
      {isRequired && <span aria-hidden="true">&thinsp;*</span>}
      <KeyRound
        className="size-4 text-muted-foreground"
        role="img"
        aria-label={translate('moonstar.user.edit.signIn.method')}
      />
    </span>
  );
};

const NotMatchingSignInHelperText = ({
  value,
}: {
  value: string | undefined;
}) => {
  const translate = useTranslate();
  const part1 = translate('moonstar.user.edit.signIn.notMatchingPart1');
  const part2 = translate('moonstar.user.edit.signIn.notMatchingPart2');
  return (
    <>
      {part1} <b>{value}</b> {part2}
    </>
  );
};
